import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Database, Search, BarChart3, Lock, Globe, Server, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.15, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const Hero = () => {
  const services = [
    {
      icon: Lock,
      title: "Private LLM Hosting",
      description: "Secure, VPC-isolated model deployment with zero data retention. Your prompts never leave your infrastructure.",
      path: "/services/sovereign-ai",
      accent: "from-orange-500 to-amber-400",
      iconBg: "bg-orange-500/10",
      borderHover: "hover:border-orange-500/50",
    },
    {
      icon: Search,
      title: "Precision RAG",
      description: "Hybrid search for 100% accuracy in financial auditing. Every response backed by deterministic citations.",
      path: "/services/precision-rag",
      accent: "from-orange-400 to-yellow-400",
      iconBg: "bg-orange-400/10",
      borderHover: "hover:border-orange-400/50",
    },
    {
      icon: Database,
      title: "AI Data Lakes",
      description: "Semantic indexing for unstructured financial documents. Turn dark data into actionable intelligence.",
      path: "/services/semantic-lakehouse",
      accent: "from-amber-500 to-orange-400",
      iconBg: "bg-amber-500/10",
      borderHover: "hover:border-amber-500/50",
    },
    {
      icon: BarChart3,
      title: "Agentic Dashboards",
      description: "Prompt-to-Visualization tools for internal data consumption. From question to insight in seconds.",
      path: "/services/agentic-dashboard",
      accent: "from-orange-600 to-red-400",
      iconBg: "bg-orange-600/10",
      borderHover: "hover:border-orange-600/50",
    },
  ];

  const trustSignals = [
    { icon: Shield, label: "SOC 2 Type II" },
    { icon: Globe, label: "GDPR Compliant" },
    { icon: Lock, label: "Zero Data Retention" },
    { icon: Server, label: "VPC Deployed" },
  ];

  return (
    <div className="pt-16">
      {/* === HERO SECTION === */}
      <section className="relative min-h-[calc(100vh-4rem)] lg:h-[calc(100vh-4rem)] flex items-center overflow-hidden">
        {/* Ambient background glow to match image tones */}
        <div className="absolute inset-0 bg-navy-950" />
        <div className="absolute top-0 right-0 w-2/3 h-full bg-gradient-to-l from-orange-900/15 via-amber-900/8 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-orange-900/10 via-transparent to-transparent pointer-events-none" />
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(249,115,22,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.2) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />

        <div className="container mx-auto px-5 sm:px-6 lg:px-12 relative z-10 py-6 sm:py-8 lg:py-10">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
            {/* LEFT — Content */}
            <div>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                custom={0}
                className="mb-3 lg:mb-4"
              >
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] sm:text-xs font-semibold tracking-wider uppercase bg-orange-500/10 text-orange-300 border border-orange-500/20">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
                  Enterprise AI Platform
                </span>
              </motion.div>

              <motion.h1
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                custom={1}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] xl:text-6xl font-extrabold leading-[1.08] tracking-tight mb-4 lg:mb-5"
              >
                <span className="text-white">Sovereign AI</span>
                <br />
                <span className="text-white">Infrastructure for</span>
                <br />
                <span className="text-gradient-blue">
                  Global Finance.
                </span>
              </motion.h1>

              <motion.p
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                custom={2}
                className="text-base sm:text-lg lg:text-xl text-slate-300/90 leading-relaxed mb-5 lg:mb-6"
              >
                Private Cloud LLM Orchestration, Precision RAG, and AI-Native Data Lakes.
                We turn complex financial data into audit-ready intelligence.
              </motion.p>

              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                custom={3}
                className="flex flex-col sm:flex-row gap-3 mb-6 lg:mb-8"
              >
                <Link
                  to="/#demo"
                  className="group inline-flex items-center justify-center px-6 py-3 sm:px-8 sm:py-3.5 bg-gradient-to-r from-[#f97316] to-[#fbce16] text-white text-sm sm:text-base font-semibold rounded-lg shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 hover:from-[#fb7a1f] hover:to-[#fbd32b] transition-all duration-300 transform hover:-translate-y-0.5"
                >
                  Request Enterprise Demo
                  <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center px-6 py-3 sm:px-8 sm:py-3.5 border border-slate-600/50 text-slate-200 text-sm sm:text-base font-semibold rounded-lg hover:bg-white/5 hover:border-slate-500/50 transition-all duration-300"
                >
                  Explore Solutions
                </Link>
              </motion.div>

              {/* Trust signals */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                custom={4}
                className="flex flex-wrap gap-4 lg:gap-6"
              >
                {trustSignals.map((signal, idx) => {
                  const Icon = signal.icon;
                  return (
                    <div key={idx} className="flex items-center gap-2 text-slate-400 text-sm">
                      <Icon className="w-4 h-4 text-orange-400/70" />
                      <span>{signal.label}</span>
                    </div>
                  );
                })}
              </motion.div>
            </div>

            {/* RIGHT — Hero Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92, x: 40 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="relative mt-2 lg:mt-0"
            >
              <div className="relative max-w-md mx-auto lg:max-w-none">
                {/* Glow behind image */}
                <div className="absolute -inset-3 bg-gradient-to-r from-orange-500/20 via-amber-500/15 to-orange-400/20 rounded-2xl blur-2xl" />
                <img
                  src="/pic01.jpg"
                  alt="AI-powered financial intelligence platform"
                  className="relative rounded-xl lg:rounded-2xl shadow-2xl shadow-orange-900/20 border border-slate-700/30 w-full h-auto object-cover max-h-[35vh] sm:max-h-[40vh] lg:max-h-[70vh] xl:max-h-none"
                />
                {/* Soft overlay to blend image with theme */}
                <div className="absolute inset-0 rounded-xl lg:rounded-2xl bg-gradient-to-t from-navy-950/40 via-transparent to-transparent pointer-events-none" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* === CORE SERVICE GRID === */}
      <section className="relative py-24 bg-navy-950">
        {/* Top edge fade */}
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-navy-950/0 to-navy-950 pointer-events-none" />

        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            custom={0}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Enterprise AI Solutions
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Purpose-built AI infrastructure for the world's most demanding financial institutions.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={fadeInUp}
                  custom={index}
                >
                  <Link
                    to={service.path}
                    className={`group relative block rounded-xl p-6 card-glass premium-border premium-elevate ${service.borderHover} transition-all duration-300 h-full`}
                  >
                    <div className={`inline-flex p-3 rounded-lg ${service.iconBg} mb-5`}>
                      <Icon className="w-6 h-6 text-orange-400" />
                    </div>

                    <h3 className="text-lg font-bold text-white mb-2">{service.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed mb-5">{service.description}</p>

                    <div className="flex items-center text-orange-400 text-sm font-medium group-hover:text-orange-300 transition-colors">
                      Learn more
                      <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* === STATS BAR === */}
      <section className="py-16 border-y border-slate-800/50 bg-navy-800/50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "99.7%", label: "Factual Accuracy" },
              { value: "< 2s", label: "Response Time" },
              { value: "0%", label: "Data Retention" },
              { value: "100%", label: "Audit Traceability" },
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                custom={idx}
              >
                <p className="text-3xl md:text-4xl font-extrabold text-gradient-blue mb-1">
                  {stat.value}
                </p>
                <p className="text-sm text-slate-400 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* === CTA SECTION === */}
      <section className="py-24 bg-navy-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-900/10 via-transparent to-amber-900/10 pointer-events-none" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">
              Ready to Transform Your AI Infrastructure?
            </h2>
            <p className="text-lg text-slate-300/80 mb-10 leading-relaxed">
              Schedule a consultation with our enterprise solutions team to discuss
              how BeeMind AI can accelerate your AI transformation.
            </p>
            <Link
              to="/#demo"
              className="group inline-flex items-center justify-center px-10 py-4 bg-gradient-to-r from-[#f97316] to-[#fbce16] text-white font-semibold rounded-lg shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 hover:from-[#fb7a1f] hover:to-[#fbd32b] transition-all duration-300 transform hover:-translate-y-0.5"
            >
              Request Enterprise Demo
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Hero;
